import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { HistorialDeCambiosComponent } from "../../admin-proyectos/historial-de-cambios/historial-de-cambios.component";
import { ProyectosService } from "../../proyectos.service";
import { EstrategiaSeguir } from "src/app/shared/model/gestion-proyectos/riesgos/estrategia-aseguir.model";
import { FaseDeteccion } from "src/app/shared/model/gestion-proyectos/riesgos/fase-deteccion.model";
import { Impacto } from "src/app/shared/model/gestion-proyectos/riesgos/impacto.model";
import { Periodicidad } from "src/app/shared/model/gestion-proyectos/riesgos/periodicidad.model";
import { Probabilidad } from "src/app/shared/model/gestion-proyectos/riesgos/probabilidad.model";
import { Riesgos } from "src/app/shared/model/gestion-proyectos/riesgos/riesgo.model";
import { SeveridadRiesgo } from "src/app/shared/model/gestion-proyectos/riesgos/severidad-riesgo.model";
import { TipoRiesgo } from "src/app/shared/model/gestion-proyectos/riesgos/tipo-riesgo.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";

const impactoCtrl = "impactoCtrl";
const probabilidadCtrl = "probabilidadCtrl";
const severidadCtrl = "severidadCtrl";

@Component({
  selector: "app-riesgo-editor-page",
  standalone: true,
  templateUrl: "./riesgo-editor-page.component.html",
  styleUrls: ["./riesgo-editor-page.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class RiesgoEditorPageComponent implements OnInit {
  formRiesgo: FormGroup;
  riesgo: Riesgos = new Riesgos();
  faseDeteccion: FaseDeteccion[] = [];
  tipoRiesgo: TipoRiesgo[] = [];
  probabilidad: Probabilidad[] = [];
  severidad: SeveridadRiesgo[] = [];
  estrategia: EstrategiaSeguir[] = [];
  periodicidad: Periodicidad[] = [];
  impacto: Impacto[] = [];
  colorSeveridad = "#cbd5e1";
  nombreSeveridad = "Pendiente";
  pageTitle = "Alta de riesgo";
  pageSubtitle =
    "Registre el riesgo del proyecto con la misma estructura visual del sistema y documente su plan de respuesta.";
  projectName = "";
  projectClient = "";
  returnUrl = "/VISUALIZARPROY";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ProyectosService,
    private msg: Msg,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private detectCambios: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.configurarContexto();
    this.obtenerfaseDeteccion();
    this.obtenertipoRiesgo();
    this.obtenerProbabilidad();
    this.obtenerSeveridad();
    this.obtenerEstrategia();
    this.obtenerPeriodicidad();
    this.obtenerImpactos();
  }

  private configurarContexto(): void {
    const idProyecto = this.route.snapshot.paramMap.get("idProyecto");
    const navigationState = window.history.state ?? {};
    const project = navigationState.projecto ?? navigationState.project ?? null;
    const riesgoState = navigationState.riesgo ?? null;

    if (!idProyecto) {
      this.msg.errorCall("No se encontró un proyecto válido para registrar el riesgo.", null, () => {
        this.navigateBack();
      });
      return;
    }

    this.riesgo.id_proyecto = idProyecto;
    this.returnUrl = navigationState.returnUrl || this.resolveDefaultReturnUrl();
    this.projectName = navigationState.projectName || project?.nombre || "";
    this.projectClient =
      navigationState.projectClient || project?.nombreCliente || "";

    if (riesgoState) {
      this.riesgo = { ...riesgoState };
      this.riesgo.id_proyecto = idProyecto;
      this.pageTitle = "Editar riesgo";
      this.pageSubtitle =
        "Actualice la clasificación y el plan de respuesta del riesgo manteniendo la homologación visual del sistema.";
      this.nombreSeveridad = this.riesgo?.severidad?.descripcion || "Pendiente";
      this.colorSeveridad = this.riesgo?.severidad?.color || "#cbd5e1";
    }
  }

  private resolveDefaultReturnUrl(): string {
    const firstSegment = this.router.url.split("?")[0].split("/").filter(Boolean)[0];
    if (["ODP", "LIDER", "AUDITORIA"].includes(firstSegment)) {
      return `/${firstSegment}/VISUALIZARPROY`;
    }
    return "/VISUALIZARPROY";
  }

  inicializarFormulario(): void {
    this.formRiesgo = this.formBuilder.group({
      faseDeteccionCtrl: ["", Validators.required],
      descripcionCtrl: ["", Validators.required],
      causaRaizCtrl: ["", Validators.required],
      tipoRiesgoCtrl: ["", Validators.required],
      probabilidadCtrl: ["", Validators.required],
      severidadCtrl: [{ value: "", disabled: true }, Validators.required],
      estrategiaCtrl: ["", Validators.required],
      periodicidadCtrl: ["", Validators.required],
      accionesSeguirCtrl: ["", Validators.required],
      responsableRecursoCtrl: ["", Validators.required],
      fechaCtrl: ["", Validators.required],
      impactoCtrl: [{ value: "", disabled: true }, Validators.required],
    });

    this.formRiesgo.get(probabilidadCtrl)?.valueChanges.subscribe((valor) => {
      const impactControl = this.formRiesgo.get(impactoCtrl);
      if (valor) {
        impactControl?.enable({ emitEvent: false });
      } else {
        impactControl?.disable({ emitEvent: false });
        this.riesgo.id_impacto = null as any;
        this.resetSeveridad();
      }
    });
  }

  private resetSeveridad(): void {
    this.riesgo.id_severidad = null as any;
    this.riesgo.calculo_severidad = null as any;
    this.colorSeveridad = "#cbd5e1";
    this.nombreSeveridad = "Pendiente";
    this.formRiesgo.get(severidadCtrl)?.setValue("");
  }

  isInvalid(controlName: string): boolean {
    const control = this.formRiesgo.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getTipoRiesgoValue(item: TipoRiesgo): number {
    return Number((item as any)?.id ?? (item as any)?.id_tipo_riesgo ?? 0);
  }

  getTipoRiesgoLabel(item: TipoRiesgo): string {
    return (
      (item as any)?.tipo_riesgo ??
      (item as any)?.nombre ??
      (item as any)?.descripcion ??
      ""
    );
  }

  obtenerfaseDeteccion(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaFaseDeteccion)
      .subscribe((res) => {
        this.faseDeteccion = res?.list ?? [];
      });
  }

  obtenertipoRiesgo(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaTipoRiesgo)
      .subscribe((res) => {
        this.tipoRiesgo = res?.list ?? [];
      });
  }

  obtenerProbabilidad(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaProbabilidad)
      .subscribe((res) => {
        this.probabilidad = res?.list ?? [];
      });
  }

  obtenerSeveridad(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaSeveridad)
      .subscribe((res) => {
        this.severidad = res?.list ?? [];
        if (this.riesgo?.id_probabilidad && this.riesgo?.id_impacto) {
          this.formRiesgo.get(impactoCtrl)?.enable({ emitEvent: false });
          this.calcularProbabilidadImpacto();
        }
      });
  }

  obtenerEstrategia(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaEstrategia)
      .subscribe((res) => {
        this.estrategia = res?.list ?? [];
      });
  }

  obtenerPeriodicidad(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaPeriodicidad)
      .subscribe((res) => {
        this.periodicidad = res?.list ?? [];
      });
  }

  obtenerImpactos(): void {
    this.service
      .consultaGeneral(UrlConstantes.consultaImpactos)
      .subscribe((res) => {
        this.impacto = res?.list ?? [];
        if (this.riesgo?.id_probabilidad) {
          this.formRiesgo.get(impactoCtrl)?.enable({ emitEvent: false });
        }
      });
  }

  calcularProbabilidadImpacto(): void {
    const impacto = this.impacto.find(
      (opcion) => opcion.id_impacto == this.riesgo.id_impacto
    );
    const probabilidad = this.probabilidad.find(
      (opcion) => opcion.id_probabilidad == this.riesgo.id_probabilidad
    );

    if (!impacto || !probabilidad) {
      this.resetSeveridad();
      return;
    }

    const probabilidadXImpacto = probabilidad.valor * impacto.valor;
    this.riesgo.calculo_severidad = probabilidadXImpacto;

    const severidadSeleccionada = this.severidad.find(
      (item) =>
        probabilidadXImpacto >= item.rango_inicial &&
        probabilidadXImpacto <= item.rango_final
    );

    if (!severidadSeleccionada) {
      this.resetSeveridad();
      return;
    }

    this.riesgo.id_severidad = severidadSeleccionada.id_severidad;
    this.colorSeveridad = severidadSeleccionada.color;
    this.nombreSeveridad = severidadSeleccionada.descripcion;
    this.formRiesgo
      .get(severidadCtrl)
      ?.setValue(severidadSeleccionada.descripcion);
    this.detectCambios.detectChanges();
  }

  guardaActualizaRiesgo(): void {
    if (this.formRiesgo.invalid) {
      this.formRiesgo.markAllAsTouched();
      this.msg.warningCall(
        "Complete los campos requeridos antes de guardar el riesgo.",
        null,
        null
      );
      return;
    }

    this.service.guardaRiesgo(this.riesgo).subscribe((res: any) => {
      if (res?.pCodRetorno === "EXITO") {
        if (this.riesgo?.id) {
          this.registroHistorialDCambios(this.riesgo.id);
        }
        this.msg.successCall("Riesgo registrado con éxito", null, () => {
          this.navigateBack();
        });
      } else {
        this.msg.errorCall("Ocurrió un error", null, null);
      }
    });
  }

  registroHistorialDCambios(idTipo: number): void {
    this.dialog.open(HistorialDeCambiosComponent, {
      disableClose: true,
      autoFocus: true,
      maxHeight: "900px",
      width: "1000px",
      panelClass: "custom-modalbox",
      data: { tipo_cambio: "RIESGO", id: idTipo },
    });
  }

  navigateBack(): void {
    this.router.navigateByUrl(this.returnUrl);
  }
}
